import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DetailsStepProps {
  recipientName: string;
  recipientEmail: string;
  senderName: string;
  senderEmail: string;
  onRecipientNameChange: (v: string) => void;
  onRecipientEmailChange: (v: string) => void;
  onSenderNameChange: (v: string) => void;
  onSenderEmailChange: (v: string) => void;
  onBack: () => void;
  onNext: () => void;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const DetailsStep: React.FC<DetailsStepProps> = ({
  recipientName, recipientEmail,
  senderName, senderEmail,
  onRecipientNameChange, onRecipientEmailChange,
  onSenderNameChange, onSenderEmailChange,
  onBack, onNext,
}) => {
  const { t } = useTranslation('quicksend');

  const isValid =
    recipientName.trim() !== '' &&
    isValidEmail(recipientEmail) &&
    senderName.trim() !== '' &&
    isValidEmail(senderEmail);

  return (
    <div className="max-w-lg mx-auto">
      <h2 className="text-2xl font-bold text-navy text-center mb-2">{t('details_title')}</h2>
      <p className="text-sm text-gray-500 text-center mb-4">{t('details_subtitle', 'Enter the details of the sender and recipient')}</p>

      <div className="flex justify-between mb-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('step_fields')}
        </Button>
        <Button onClick={onNext} disabled={!isValid} className="gap-2 bg-green hover:bg-green-dark text-white">
          {t('step_send')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Recipient */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 mb-4">
        <h3 className="font-semibold text-navy mb-1">{t('details_recipient_heading')}</h3>
        <p className="text-xs text-gray-500 mb-3">{t('details_recipient_description')}</p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="recipientName">{t('details_name')} *</Label>
            <Input id="recipientName" value={recipientName} onChange={(e) => onRecipientNameChange(e.target.value)} placeholder="John Smith" />
          </div>
          <div>
            <Label htmlFor="recipientEmail">{t('details_email')} *</Label>
            <Input id="recipientEmail" type="email" value={recipientEmail} onChange={(e) => onRecipientEmailChange(e.target.value)} placeholder="john@example.com" />
          </div>
        </div>
      </div>

      {/* Sender */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
        <h3 className="font-semibold text-navy mb-1">{t('details_sender_heading')}</h3>
        <p className="text-xs text-gray-500 mb-3">{t('details_sender_description')}</p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="senderName">{t('details_name')} *</Label>
            <Input id="senderName" value={senderName} onChange={(e) => onSenderNameChange(e.target.value)} placeholder="Jane Doe" />
          </div>
          <div>
            <Label htmlFor="senderEmail">{t('details_email')} *</Label>
            <Input id="senderEmail" type="email" value={senderEmail} onChange={(e) => onSenderEmailChange(e.target.value)} placeholder="jane@example.com" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsStep;
